import { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Papa from "papaparse";
import { getStats } from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Button,
  Alert,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
} from "@/components/ui";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import {
  AnimatedNumber,
  useAnimatedValue,
} from "@/components/AnimatedNumber";
import AppLoading from "@/components/AppLoading";
import { FaCheckCircle } from "react-icons/fa";
import PlanChip from "../../../components/PlanChip";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import ApiIcon from "@mui/icons-material/Api";

const ANIMATION_DURATION_MS = 600;
const STAT_COUNT_DURATION_MS = 800;
const QUOTA_COUNT_DURATION_MS = 700;
const PERIOD_OPTIONS = [7, 30, 90, 0]; // 0 = All

const CompanyUsage = () => {
  const { t, isRTL } = useApp();
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadStats = useCallback(async (periodDays = days) => {
    setLoading(true);
    try {
      const apiDays = periodDays === 0 ? 3650 : periodDays; // 0 = All (~10 years)
      const data = await getStats(apiDays);
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadStats(days);
  }, [days]);

  const contractsUsed = stats?.usage?.currentPeriod?.contractsCreated || 0;
  const contractsSigned = stats?.usage?.currentPeriod?.contractsSigned || 0;
  const contractsExpired = stats?.usage?.currentPeriod?.contractsExpired || 0;
  const smsSent = stats?.usage?.currentPeriod?.smsSent || 0;
  const emailsSent = stats?.usage?.currentPeriod?.emailsSent || 0;
  const apiCalls = stats?.usage?.currentPeriod?.apiCalls || 0;
  const storageUsedMb = stats?.usage?.currentPeriod?.storageUsedMb || 0;
  const contractsLimit =
    stats?.subscription?.limits?.contractsPerMonth || 100;
  const templatesLimit = stats?.subscription?.limits?.templatesAllowed || 5;
  const teamLimit = stats?.subscription?.limits?.teamMembers || 2;
  const storageLimit = stats?.subscription?.limits?.storageLimit ?? 500;
  const contractsPercent = Math.round((contractsUsed / contractsLimit) * 100);
  const signRate =
    contractsUsed > 0
      ? Math.round((contractsSigned / contractsUsed) * 100)
      : 0;
  const pricePerDoc = stats?.billing?.pricePerDocument || 5;
  const includedDocs = stats?.billing?.documentsIncluded || 50;
  const extraDocs = Math.max(0, contractsUsed - includedDocs);
  const extraCost = extraDocs * pricePerDoc;
  const plan = stats?.subscription?.plan || "starter";
  const subStatus = stats?.subscription?.status || "active";
  const templatesUsed = stats?.templatesCount ?? 0;
  const teamUsed = stats?.teamMembersCount ?? 0;
  const sandbox = stats?.sandbox;
  const previousPeriod = stats?.previousPeriod;
  const contractStatusCounts = stats?.contracts || {};
  const dailyData = stats?.daily || [];
  const webhooks = stats?.webhooks || {};
  const lastApiCallAt = stats?.usage?.lastApiCallAt;
  const lastContractCreatedAt = stats?.usage?.lastContractCreatedAt;
  const allTimeCreated = stats?.usage?.allTime?.contractsCreated ?? 0;
  const allTimeSigned = stats?.usage?.allTime?.contractsSigned ?? 0;

  const animatedContractsCount = useAnimatedValue(
    contractsUsed,
    QUOTA_COUNT_DURATION_MS
  );
  const animatedTemplatesCount = useAnimatedValue(
    templatesUsed,
    QUOTA_COUNT_DURATION_MS
  );
  const animatedTeamCount = useAnimatedValue(
    teamUsed,
    QUOTA_COUNT_DURATION_MS
  );
  const animatedStorageUsed = useAnimatedValue(
    storageUsedMb,
    QUOTA_COUNT_DURATION_MS
  );

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getProgressColor = (pct) =>
    pct > 90 ? "error" : pct > 70 ? "warning" : "primary";

  // Track background: subtle black in dark mode, light gray in light mode
  const isDark = theme.palette.mode === "dark";
  const trackBg = isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.08)";

  // Bold, acrylic-style colors for quota progress bars (fill + track)
  const quotaBarStyles = {
    contracts: {
      normal: {
        "& .MuiLinearProgress-bar": { bgcolor: "#5B9BD5" },
        bgcolor: trackBg,
      },
      warning: {
        "& .MuiLinearProgress-bar": { bgcolor: "#E5B84C" },
        bgcolor: trackBg,
      },
      error: {
        "& .MuiLinearProgress-bar": { bgcolor: "#E57373" },
        bgcolor: trackBg,
      },
    },
    templates: {
      "& .MuiLinearProgress-bar": { bgcolor: "#D4A84B" },
      bgcolor: trackBg,
    },
    team: {
      "& .MuiLinearProgress-bar": { bgcolor: "#9C6ADE" },
      bgcolor: trackBg,
    },
    storage: {
      "& .MuiLinearProgress-bar": { bgcolor: "#5B9BD5" },
      bgcolor: trackBg,
    },
  };

  const getStatusLabel = (s) => {
    const map = {
      trial: isRTL ? "תקופת ניסיון" : "Trial",
      active: isRTL ? "פעיל" : "Active",
      cancelled: isRTL ? "מבוטל" : "Cancelled",
      expired: isRTL ? "פג תוקף" : "Expired",
    };
    return map[s] || s;
  };

  const handlePeriodChange = (_, newDays) => {
    if (newDays != null) setDays(newDays);
  };

  const exportCSV = () => {
    const rows = [
      [isRTL ? "מדד" : "Metric", isRTL ? "ערך" : "Value"],
      [isRTL ? "חוזים שנוצרו (תקופה)" : "Contracts created (period)", contractsUsed],
      [isRTL ? "חוזים שנחתמו" : "Contracts signed", contractsSigned],
      [isRTL ? "חוזים שפג תוקף" : "Contracts expired", contractsExpired],
      [isRTL ? "SMS נשלחו" : "SMS sent", smsSent],
      [isRTL ? "אימיילים נשלחו" : "Emails sent", emailsSent],
      [isRTL ? "קריאות API" : "API calls", apiCalls],
      [isRTL ? "אחסון (MB)" : "Storage (MB)", storageUsedMb],
      [isRTL ? "מכסה חוזים" : "Contracts limit", contractsLimit],
      [isRTL ? "תקופה (ימים)" : "Period (days)", days === 0 ? (isRTL ? "הכל" : "All") : days],
    ];
    const csv = Papa.unparse(rows);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const printEl = document.getElementById("usage-print-area");
    if (!printEl) return;
    const prevDisplay = printEl.style.display;
    printEl.style.display = "block";
    window.print();
    printEl.style.display = prevDisplay;
  };

  const statCards = [
    {
      label: isRTL ? "חוזים שנוצרו" : "Contracts Created",
      value: contractsUsed,
      sub: `${isRTL ? "מתוך" : "of"} ${contractsLimit} ${isRTL ? "בתקופה" : "this period"}`,
      Icon: DescriptionIcon,
      color: "#3b82f6",
    },
    {
      label: isRTL ? "חוזים שנחתמו" : "Contracts Signed",
      value: contractsSigned,
      sub: `${signRate}% ${isRTL ? "שיעור חתימה" : "signature rate"}`,
      Icon: CheckCircleOutlineIcon,
      color: "#22c55e",
    },
    {
      label: isRTL ? "חוזים שפג תוקף" : "Contracts Expired",
      value: contractsExpired,
      sub: isRTL ? "לא נחתמו לפני תפוגה" : "unsigned before expiry",
      Icon: ScheduleIcon,
      color: "#f59e0b",
    },
    {
      label: isRTL ? "הודעות SMS" : "SMS Sent",
      value: smsSent,
      sub: isRTL ? "בתקופה הנוכחית" : "this period",
      Icon: SmsIcon,
      color: "#a855f7",
    },
    {
      label: isRTL ? "אימיילים נשלחו" : "Emails Sent",
      value: emailsSent,
      sub: isRTL ? "בתקופה הנוכחית" : "this period",
      Icon: EmailIcon,
      color: "#06b6d4",
    },
    {
      label: isRTL ? "קריאות API" : "API Calls",
      value: apiCalls,
      sub: isRTL ? "בתקופה הנוכחית" : "this period",
      Icon: ApiIcon,
      color: "#eab308",
    },
  ];

  const statusLabels = {
    pending: isRTL ? "ממתין" : "Pending",
    viewed: isRTL ? "נצפה" : "Viewed",
    signed: isRTL ? "נחתם" : "Signed",
    expired: isRTL ? "פג תוקף" : "Expired",
    cancelled: isRTL ? "בוטל" : "Cancelled",
  };

  if (loading && !stats) {
    return <AppLoading fullPage />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        pt: 2,
      }}
    >
      {loading && (
        <AppLoading overlay size="medium" sx={{ borderRadius: 1 }} />
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
        flexWrap="wrap"
      >
        <Typography variant="h5" fontWeight={600}>
        {isRTL ? "שימוש וחיוב" : "Usage & Billing"}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="contained"
            onClick={() => window.location.reload()}
            {...(isRTL
              ? { endIcon: <RefreshIcon /> }
              : { startIcon: <RefreshIcon /> })}
            aria-label={isRTL ? "רענן" : "Refresh"}
            sx={{
              ...(theme.palette.mode === "light"
                ? {
                    bgcolor: "#000",
                    color: "#fff",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
                    "&:hover": { bgcolor: "grey.800", boxShadow: "none" },
                  }
                : {
                    bgcolor: "#fff",
                    color: "#000",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#000" },
                    "&:hover": { bgcolor: "grey.100", boxShadow: "none" },
                  }),
              borderRadius: "24px",
              boxShadow: "none",
              border: "1px solid",
              borderColor: theme.palette.mode === "light" ? "transparent" : "divider",
            }}
          >
            {isRTL ? "רענן" : "REFRESH"}
          </Button>
          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            aria-label="period"
            sx={{
              "& .MuiToggleButton-root": {
                px: 1.75,
              },
              ...(isRTL && {
                flexDirection: "row-reverse",
                "& .MuiToggleButtonGroup-grouped": {
                  flexDirection: "row-reverse",
                },
              }),
            }}
          >
            {(isRTL ? [...PERIOD_OPTIONS].reverse() : PERIOD_OPTIONS).map(
              (d) => (
                <ToggleButton
                  key={d}
                  value={d}
                  aria-label={d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
                  sx={isRTL ? { direction: "rtl" } : undefined}
                >
                  {d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
                </ToggleButton>
              )
            )}
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="contained"
            {...(isRTL
              ? { endIcon: <FileDownloadIcon /> }
              : { startIcon: <FileDownloadIcon /> })}
            onClick={exportCSV}
            sx={{
              bgcolor: "#22c55e",
              color: "#fff",
              borderRadius: "24px",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#16a34a",
                boxShadow: "none",
              },
            }}
          >
            CSV
          </Button>
          <Button
            size="small"
            variant="contained"
            {...(isRTL
              ? { endIcon: <PictureAsPdfIcon /> }
              : { startIcon: <PictureAsPdfIcon /> })}
            onClick={exportPDF}
            sx={{
              bgcolor: "#DC2626",
              color: "#fff",
              borderRadius: "24px",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#B91C1C",
                boxShadow: "none",
              },
            }}
          >
            PDF
          </Button>
          <Button
            size="small"
            component="a"
            href="/docs/api#usage"
            target="_blank"
            rel="noopener noreferrer"
            {...(isRTL
              ? { endIcon: <LinkIcon /> }
              : { startIcon: <LinkIcon /> })}
            variant="outlined"
          >
            {isRTL ? "תיעוד API" : "API Docs"}
          </Button>
        </Stack>
      </Stack>

      {contractsPercent >= 80 && (
        <Alert
          severity={contractsPercent >= 90 ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {contractsPercent >= 90
            ? isRTL
              ? `הגעת ל־${contractsPercent}% ממכסת החוזים החודשית. שדרג תוכנית או צור קשר.`
              : `You've reached ${contractsPercent}% of your monthly contract quota. Upgrade or contact support.`
            : isRTL
              ? `מתקרבים למכסה: ${contractsPercent}% משימוש בחוזים החודשיים.`
              : `Approaching quota: ${contractsPercent}% of monthly contracts used.`}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ "&:last-child": { pb: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              gap={1.5}
              flexWrap="wrap"
            >
              <PlanChip plan={plan} />
              {subStatus === "active" ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ direction: "ltr" }}
                >
                  <Chip
                    label={getStatusLabel(subStatus)}
                    color="success"
                    variant="outlined"
                    sx={{ px: 0.75, py: 0.75, fontSize: "0.9375rem" }}
                    icon={<Box sx={{ color: "success.main", display: "inline-flex"}}><FaCheckCircle /></Box>}
                  />
                </Stack>
              ) : (
                <Chip
                  label={getStatusLabel(subStatus)}
                  color={
                    subStatus === "trial"
                      ? "warning"
                      : "default"
                  }
                  variant="outlined"
                  sx={{ px: 0.75, py: 0.75, fontSize: "0.9375rem" }}
                />
              )}
              {subStatus === "trial" && stats?.subscription?.trialEndsAt && (
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "ניסיון עד" : "Trial until"}{" "}
                  {formatDate(stats.subscription.trialEndsAt)}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {isRTL ? "תקופת חיוב:" : "Billing period:"}{" "}
              {formatDate(stats?.subscription?.currentPeriodStart)} —{" "}
              {formatDate(stats?.subscription?.currentPeriodEnd)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
            lg: "repeat(6, 1fr)",
          },
          gap: 2,
          mb: 2,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {statCards.map((item, i) => (
          <Card
            key={i}
            variant="outlined"
            sx={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                gap={1.5}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 35,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: `${item.color}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    userSelect: "none",
                  }}
                >
                  <item.Icon sx={{ fontSize: "1.25rem", color: item.color }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                  noWrap
                  sx={{ fontSize: "1rem" }}
                >
                  {item.label}
                </Typography>
              </Stack>
              <AnimatedNumber
                value={item.value}
                duration={STAT_COUNT_DURATION_MS}
                component={Typography}
                variant="h4"
                fontWeight={700}
              />
              <Typography variant="caption" color="text.secondary">
                {item.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ניצולת מכסות + השוואה לתקופה קודמת — באותה שורה */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "ניצולת מכסות" : "Quota Usage"}
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{isRTL ? "חוזים" : "Contracts"}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {isRTL ? `${contractsLimit} / ${animatedContractsCount}` : `${animatedContractsCount} / ${contractsLimit}`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={contractsLimit ? Math.min(100, (animatedContractsCount / contractsLimit) * 100) : 0}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    "& .MuiLinearProgress-bar": { transition: `transform ${ANIMATION_DURATION_MS}ms ease-out` },
                    ...(contractsPercent > 90 ? quotaBarStyles.contracts.error : contractsPercent > 70 ? quotaBarStyles.contracts.warning : quotaBarStyles.contracts.normal),
                  }}
                />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{isRTL ? "תבניות" : "Templates"}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {isRTL ? `${templatesLimit} / ${animatedTemplatesCount}` : `${animatedTemplatesCount} / ${templatesLimit}`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={templatesLimit ? Math.min(100, (animatedTemplatesCount / templatesLimit) * 100) : 0}
                  sx={{ height: 8, borderRadius: 1, ...quotaBarStyles.templates }}
                />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{isRTL ? "חברי צוות" : "Team Members"}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {isRTL ? `${teamLimit} / ${animatedTeamCount}` : `${animatedTeamCount} / ${teamLimit}`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={teamLimit ? Math.min(100, (animatedTeamCount / teamLimit) * 100) : 0}
                  sx={{ height: 8, borderRadius: 1, ...quotaBarStyles.team }}
                />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{isRTL ? "אחסון (MB)" : "Storage (MB)"}</Typography>
                  <Typography variant="body2" fontWeight={600}>{animatedStorageUsed} / {storageLimit}</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={storageLimit ? Math.min(100, (animatedStorageUsed / storageLimit) * 100) : 0}
                  sx={{ height: 8, borderRadius: 1, ...quotaBarStyles.storage }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              {isRTL ? "השוואה לתקופה קודמת" : "Comparison to previous period"}
            </Typography>
            {previousPeriod ? (
              <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.5, px: 1, fontSize: "0.8125rem" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>{isRTL ? "מדד" : "Metric"}</TableCell>
                      <TableCell align="center" sx={{ color: "text.secondary", fontWeight: 600 }}>{isRTL ? "תקופה קודמת" : "Previous"}</TableCell>
                      <TableCell align="center" sx={{ color: "text.secondary", fontWeight: 600 }}>{isRTL ? "תקופה נוכחית" : "Current"}</TableCell>
                      <TableCell align="center" sx={{ color: "text.secondary", fontWeight: 600 }}>{isRTL ? "שינוי" : "Change"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ color: "text.secondary" }}>{isRTL ? "נוצרו" : "Created"}</TableCell>
                      <TableCell align="center">
                        <AnimatedNumber value={previousPeriod.contractsCreated ?? 0} duration={STAT_COUNT_DURATION_MS} component="span" />
                      </TableCell>
                      <TableCell align="center">
                        <AnimatedNumber value={contractsUsed ?? 0} duration={STAT_COUNT_DURATION_MS} component="span" />
                      </TableCell>
                      <TableCell align="center">
                        {previousPeriod.contractsCreated > 0 ? (() => {
                          const pct = Math.round(((contractsUsed - previousPeriod.contractsCreated) / previousPeriod.contractsCreated) * 100);
                          const isUp = pct >= 0;
                          return (
                            <Typography component="span" variant="body2" color={isUp ? "success.main" : "error.main"} fontWeight={600}>
                              {isUp ? "+" : ""}{pct}% {isUp ? "↑" : "↓"}
                            </Typography>
                          );
                        })() : (
                          <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ color: "text.secondary" }}>{isRTL ? "נחתמו" : "Signed"}</TableCell>
                      <TableCell align="center">
                        <AnimatedNumber value={previousPeriod.contractsSigned ?? 0} duration={STAT_COUNT_DURATION_MS} component="span" />
                      </TableCell>
                      <TableCell align="center">
                        <AnimatedNumber value={contractsSigned ?? 0} duration={STAT_COUNT_DURATION_MS} component="span" />
                      </TableCell>
                      <TableCell align="center">
                        {previousPeriod.contractsSigned > 0 ? (() => {
                          const pct = Math.round(((contractsSigned - previousPeriod.contractsSigned) / previousPeriod.contractsSigned) * 100);
                          const isUp = pct >= 0;
                          return (
                            <Typography component="span" variant="body2" color={isUp ? "success.main" : "error.main"} fontWeight={600}>
                              {isUp ? "+" : ""}{pct}% {isUp ? "↑" : "↓"}
                            </Typography>
                          );
                        })() : (
                          <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1, pb: 0.5 }}>
                  {isRTL ? "תקופה קודמת:" : "Previous period:"} {formatDate(previousPeriod.periodStart)} – {formatDate(previousPeriod.periodEnd)}
                </Typography>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">{isRTL ? "אין נתונים להשוואה" : "No comparison data"}</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 2,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "סיכום חיוב" : "Billing Summary"}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "מסמכים כלולים בתוכנית" : "Documents included"}
                </Typography>
                <AnimatedNumber
                  value={includedDocs}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "מסמכים שנוצלו" : "Documents used"}
                </Typography>
                <AnimatedNumber
                  value={contractsUsed}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "מסמכים נוספים" : "Extra documents"}
                </Typography>
                <AnimatedNumber
                  value={extraDocs}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                  color={extraDocs > 0 ? "warning.main" : undefined}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "מחיר למסמך נוסף" : "Price per extra doc"}
                </Typography>
                <Typography fontWeight={600}>
                  {isRTL ? (
                    <>
                      <AnimatedNumber
                        value={pricePerDoc}
                        duration={STAT_COUNT_DURATION_MS}
                        component="span"
                      />{" "}
                      ₪
                    </>
                  ) : (
                    <>
                      ₪{" "}
                      <AnimatedNumber
                        value={pricePerDoc}
                        duration={STAT_COUNT_DURATION_MS}
                        component="span"
                      />
                    </>
                  )}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ pt: 1.5, mt: 1, borderTop: 1, borderColor: "divider" }}
              >
                <Typography fontWeight={700}>
                  {isRTL ? 'סה"כ לתשלום נוסף' : "Total extra charges"}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={extraCost > 0 ? "error.main" : "success.main"}
                >
                  {isRTL ? (
                    <>
                      <AnimatedNumber
                        value={extraCost}
                        duration={STAT_COUNT_DURATION_MS}
                        component="span"
                      />{" "}
                      ₪
                    </>
      ) : (
        <>
                      ₪{" "}
                      <AnimatedNumber
                        value={extraCost}
                        duration={STAT_COUNT_DURATION_MS}
                        component="span"
                      />
                    </>
                  )}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
            </Card>

        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "מאז ומעולם" : "All time"}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "חוזים שנוצרו" : "Contracts created"}
                </Typography>
                <AnimatedNumber
                  value={allTimeCreated}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "חוזים שנחתמו" : "Contracts signed"}
                </Typography>
                <AnimatedNumber
                  value={allTimeSigned}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                />
              </Stack>
            </Stack>
            {(lastApiCallAt || lastContractCreatedAt) && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mt: 2, mb: 0.5 }}
                >
                  {isRTL ? "פעילות אחרונה" : "Last activity"}
                </Typography>
                <Stack spacing={0.5}>
                  {lastContractCreatedAt && (
                    <Typography variant="caption" display="block">
                      {isRTL ? "חוזה נוצר:" : "Contract created:"}{" "}
                      {formatDateTime(lastContractCreatedAt)}
                    </Typography>
                  )}
                  {lastApiCallAt && (
                    <Typography variant="caption" display="block">
                      {isRTL ? "קריאת API:" : "API call:"}{" "}
                      {formatDateTime(lastApiCallAt)}
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </CardContent>
            </Card>

        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "פירוט לפי סטטוס חוזים" : "Contracts by status"}
            </Typography>
            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{isRTL ? "סטטוס" : "Status"}</TableCell>
                    <TableCell align="right">{isRTL ? "כמות" : "Count"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["pending", "viewed", "signed", "expired", "cancelled"].map(
                    (status) => (
                      <TableRow key={status}>
                        <TableCell>{statusLabels[status] || status}</TableCell>
                        <TableCell align="right">
                          <AnimatedNumber
                            value={contractStatusCounts[status] ?? 0}
                            duration={STAT_COUNT_DURATION_MS}
                            component="span"
                          />
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ width: "100%", minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "Webhooks" : "Webhooks"}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "סה״כ" : "Total"}
                </Typography>
                <AnimatedNumber
                  value={webhooks.total ?? 0}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "הצלחות" : "Success"}
                </Typography>
                <AnimatedNumber
                  value={webhooks.success ?? 0}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                  color="success.main"
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "כשלונות" : "Failed"}
                </Typography>
                <AnimatedNumber
                  value={webhooks.failed ?? 0}
                  duration={STAT_COUNT_DURATION_MS}
                  component={Typography}
                  fontWeight={600}
                  color="error.main"
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "שיעור הצלחה" : "Success rate"}
                </Typography>
                <Typography fontWeight={600}>
                  <AnimatedNumber
                    value={webhooks.successRate ?? 0}
                    duration={STAT_COUNT_DURATION_MS}
                    component="span"
                  />
                  %
                </Typography>
              </Stack>
              {(webhooks.avgResponseTimeMs ?? 0) > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {isRTL ? "זמן תגובה ממוצע" : "Avg response time"}
                  </Typography>
                  <Typography fontWeight={600}>
                    <AnimatedNumber
                      value={webhooks.avgResponseTimeMs}
                      duration={STAT_COUNT_DURATION_MS}
                      component="span"
                    />{" "}
                    ms
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
            </Card>

      </Box>

      {sandbox?.enabled && (
        <Card variant="outlined" sx={{ mb: 2 }} color="warning">
          <CardContent>
            <Typography variant="h6" gutterBottom color="warning.main">
              {isRTL ? "מצב Sandbox" : "Sandbox mode"}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "חוזים היום" : "Contracts today"}
                </Typography>
                <Typography fontWeight={600}>
                  <AnimatedNumber
                    value={sandbox.usage?.contractsToday ?? 0}
                    duration={STAT_COUNT_DURATION_MS}
                    component="span"
                  />{" "}
                  / {sandbox.limits?.contractsPerDay ?? 10}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "סה״כ חוזים (סנדבוקס)" : "Total contracts (sandbox)"}
                </Typography>
                <Typography fontWeight={600}>
                  <AnimatedNumber
                    value={sandbox.usage?.totalContracts ?? 0}
                    duration={STAT_COUNT_DURATION_MS}
                    component="span"
                  />{" "}
                  / {sandbox.limits?.totalContracts ?? 50}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isRTL ? "שימוש יומי" : "Daily usage"}
          </Typography>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={dailyData}
                margin={{ top: 16, right: 0, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={theme.palette.divider}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => {
                    const d = new Date(v);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  width={28}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                  labelFormatter={(v) => formatDate(v)}
                />
                <Bar
                  dataKey="created"
                  fill={theme.palette.primary.main}
                  radius={[4, 4, 0, 0]}
                  name={isRTL ? "נוצרו" : "Created"}
                  fillOpacity={0.75}
                />
                <Bar
                  dataKey="signed"
                  fill={theme.palette.success.main}
                  radius={[4, 4, 0, 0]}
                  name={isRTL ? "נחתמו" : "Signed"}
                  fillOpacity={0.75}
                />
                <Bar
                  dataKey="expired"
                  fill={theme.palette.warning.main}
                  radius={[4, 4, 0, 0]}
                  name={isRTL ? "פג תוקף" : "Expired"}
                  fillOpacity={0.75}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                {isRTL ? "אין נתונים יומיים בתקופה הנבחרת" : "No daily data for selected period"}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box
        id="usage-print-area"
        sx={{ display: "none" }}
        className="print-only"
      >
        <Typography variant="h6">
          {isRTL ? "סיכום שימוש וחיוב" : "Usage & Billing Summary"}
        </Typography>
        <Typography variant="body2">
          {formatDate(new Date())} · {plan} · {getStatusLabel(subStatus)}
        </Typography>
        <Typography variant="body2">
          {isRTL ? "חוזים:" : "Contracts:"} {contractsUsed} / {contractsLimit} ·{" "}
          {isRTL ? "נחתמו:" : "Signed:"} {contractsSigned} ·{" "}
          {isRTL ? "פג תוקף:" : "Expired:"} {contractsExpired}
        </Typography>
        <Typography variant="body2">
          SMS: {smsSent} · {isRTL ? "אימיילים:" : "Emails:"} {emailsSent} · API:{" "}
          {apiCalls}
        </Typography>
        <Typography variant="body2">
          {isRTL ? "סה״כ לתשלום נוסף:" : "Total extra charges:"} ₪{extraCost}
        </Typography>
      </Box>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #usage-print-area, #usage-print-area * { visibility: visible; }
          #usage-print-area { display: block !important; position: absolute; left: 0; top: 0; }
        }
      `}</style>
    </Box>
  );
};

export default CompanyUsage;
